import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import RideApi from "Services/Ride";
import PlaceApi from "Services/Place";

const RideWaypointCreateModal = ({ openDialog, handleClose, waypointId = "" }) => {
  const dispatch = useDispatch();
  const [rides, setRides] = useState([]);
  const [places, setPlaces] = useState([]);
  const isEditMode = Boolean(waypointId);

  const normalizeList = (res, key) => {
    const payload = res?.data;
    if (Array.isArray(payload?.[key])) return payload[key] || [];
    if (Array.isArray(payload?.data?.data)) return payload.data.data || [];
    if (Array.isArray(payload?.data)) return payload.data || [];
    return [];
  };

  useEffect(() => {
    if (!openDialog) return;

    RideApi.getRideList({ page: 1, limit: "unlimited", search: "" }).then(
      (res) => setRides(normalizeList(res, "rides")),
      () => setRides([])
    );

    PlaceApi.getPlaceList({ page: 1, limit: "unlimited", search: "" }).then(
      (res) => setPlaces(normalizeList(res, "places")),
      () => setPlaces([])
    );
  }, [openDialog]);

  useEffect(() => {
    if (openDialog && !isEditMode) {
      formik.resetForm();
    }
  }, [openDialog, isEditMode]);

  useEffect(() => {
    if (!openDialog || !isEditMode || !waypointId) return;

    dispatch(showLoader("Loading please wait..."));
    RideApi.getRideWaypointById(waypointId).then(
      (res) => {
        dispatch(hideLoader());
        const data = res?.data || {};
        formik.setValues({
          ride_id: data?.ride_id || "",
          place_id: data?.place_id || "",
          sequence_order: Number(data?.sequence_order ?? 1),
          arrival_offset_min: Number(data?.arrival_offset_min ?? 0),
          price_override: Number(data?.price_override ?? 0),
        });
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to load ride waypoint details",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch, isEditMode, openDialog, waypointId]);

  const formik = useFormik({
    initialValues: {
      ride_id: "",
      place_id: "",
      sequence_order: 1,
      arrival_offset_min: 15,
      price_override: 2500,
    },
    validationSchema: yup.object({
      ride_id: yup.string().required("Ride is required"),
      place_id: yup.string().required("Place is required"),
      sequence_order: yup.number().required("Sequence order is required").min(1, "Minimum is 1"),
      arrival_offset_min: yup.number().required("Arrival offset is required").min(0, "Minimum is 0"),
      price_override: yup.number().required("Price override is required").min(0, "Minimum is 0"),
    }),
    onSubmit: (values) => {
      dispatch(showLoader("Loading please wait..."));
      const payload = {
        ride_id: values.ride_id,
        place_id: values.place_id,
        sequence_order: Number(values.sequence_order),
        arrival_offset_min: Number(values.arrival_offset_min),
        price_override: Number(values.price_override),
      };

      const request = isEditMode ? RideApi.updateRideWaypoint(waypointId, payload) : RideApi.createRideWaypoint(payload);
      request.then(
        (res) => {
          dispatch(hideLoader());
          if (res?.status === 200 || res?.status === 201) {
            dispatch(
              showSnackbar({
                message: res?.data?.message || (isEditMode ? "Ride waypoint updated successfully" : "Ride waypoint created successfully"),
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "success",
              })
            );
            formik.resetForm();
            handleClose(true);
          }
        },
        (err) => {
          dispatch(hideLoader());
          dispatch(
            showSnackbar({
              message: err?.response?.data?.message || (isEditMode ? "Error while updating ride waypoint" : "Error while creating ride waypoint"),
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "error",
            })
          );
        }
      );
    },
  });

  const getRideId = (ride) => ride?.id || ride?._id || "";
  const getPlaceId = (place) => place?.id || place?._id || "";
  return (
    <Dialog open={openDialog} onClose={() => handleClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>{isEditMode ? "Edit Ride Waypoint" : "Add Ride Waypoint"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Ride"
              name="ride_id"
              value={formik.values.ride_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.ride_id && Boolean(formik.errors.ride_id)}
              helperText={formik.touched.ride_id && formik.errors.ride_id}
            >
              <MenuItem value="">Select Ride</MenuItem>
              {rides.map((ride) => {
                const rideId = getRideId(ride);
                if (!rideId) return null;
                const origin = ride?.places_rides_origin_place_idToplaces?.name || ride?.origin_place_id || "-";
                const destination = ride?.places_rides_destination_place_idToplaces?.name || ride?.destination_place_id || "-";
                return (
                  <MenuItem key={rideId} value={rideId}>
                    {`${origin} - ${destination}`}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Place"
              name="place_id"
              value={formik.values.place_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.place_id && Boolean(formik.errors.place_id)}
              helperText={formik.touched.place_id && formik.errors.place_id}
            >
              <MenuItem value="">Select Place</MenuItem>
              {places.map((place) => {
                const placeId = getPlaceId(place);
                if (!placeId) return null;
                const label = `${place?.name || placeId}${place?.addressLocality ? ` - ${place.addressLocality}` : ""}`;
                return (
                  <MenuItem key={placeId} value={placeId}>
                    {label}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Sequence Order"
              name="sequence_order"
              value={formik.values.sequence_order}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.sequence_order && Boolean(formik.errors.sequence_order)}
              helperText={formik.touched.sequence_order && formik.errors.sequence_order}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Arrival Offset (min)"
              name="arrival_offset_min"
              value={formik.values.arrival_offset_min}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.arrival_offset_min && Boolean(formik.errors.arrival_offset_min)}
              helperText={formik.touched.arrival_offset_min && formik.errors.arrival_offset_min}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Price Override"
              name="price_override"
              value={formik.values.price_override}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.price_override && Boolean(formik.errors.price_override)}
              helperText={formik.touched.price_override && formik.errors.price_override}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(false)}>Cancel</Button>
        <Button variant="contained" onClick={formik.handleSubmit}>{isEditMode ? "Update" : "Add"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RideWaypointCreateModal;
